/**
 * MembershipModal — Create / Edit membership modal
 *
 * Schema fields (from schema-compliance.ts / compliance-memberships route):
 *  - membershipType: industry | professional | aggregator | other
 *  - membershipStatus: active | expired | suspended | cancelled (NO pending_renewal)
 *  - membershipNumber (NOT memberNumber)
 *  - contactName (NOT memberName), contactEmail, contactPhone
 *  - No membershipLevel field in schema
 *  - HAS: benefitsSummary, certificateUrl, annualFee, joinDate, renewalDate, expiryDate
 */

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MembershipData {
  id?: string;
  membershipType?: string;
  organisationName?: string;
  membershipNumber?: string | null;
  membershipStatus?: string;
  joinDate?: string | null;
  renewalDate?: string | null;
  expiryDate?: string | null;
  annualFee?: string | null;
  benefitsSummary?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  certificateUrl?: string | null;
}

interface MembershipModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingMembership?: MembershipData | null;
  onSuccess?: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MEMBERSHIP_TYPES = [
  { value: "industry", label: "Industry" },
  { value: "professional", label: "Professional" },
  { value: "aggregator", label: "Aggregator" },
  { value: "other", label: "Other" },
];

const MEMBERSHIP_STATUSES = [
  { value: "active", label: "Active" },
  { value: "expired", label: "Expired" },
  { value: "suspended", label: "Suspended" },
  { value: "cancelled", label: "Cancelled" },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MembershipModal({
  open,
  onOpenChange,
  editingMembership,
  onSuccess,
}: MembershipModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const isEditMode = !!editingMembership?.id;

  // Form state
  const [membershipType, setMembershipType] = useState("");
  const [organisationName, setOrganisationName] = useState("");
  const [membershipNumber, setMembershipNumber] = useState("");
  const [membershipStatus, setMembershipStatus] = useState("active");
  const [joinDate, setJoinDate] = useState("");
  const [renewalDate, setRenewalDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [annualFee, setAnnualFee] = useState("");
  const [benefitsSummary, setBenefitsSummary] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [certificateUrl, setCertificateUrl] = useState("");

  // Reset / pre-populate form when modal opens or editingMembership changes
  useEffect(() => {
    if (open && editingMembership) {
      setMembershipType(editingMembership.membershipType ?? "");
      setOrganisationName(editingMembership.organisationName ?? "");
      setMembershipNumber(editingMembership.membershipNumber ?? "");
      setMembershipStatus(editingMembership.membershipStatus ?? "active");
      setJoinDate(editingMembership.joinDate ?? "");
      setRenewalDate(editingMembership.renewalDate ?? "");
      setExpiryDate(editingMembership.expiryDate ?? "");
      setAnnualFee(editingMembership.annualFee ?? "");
      setBenefitsSummary(editingMembership.benefitsSummary ?? "");
      setContactName(editingMembership.contactName ?? "");
      setContactEmail(editingMembership.contactEmail ?? "");
      setContactPhone(editingMembership.contactPhone ?? "");
      setCertificateUrl(editingMembership.certificateUrl ?? "");
    } else if (open && !editingMembership) {
      setMembershipType("");
      setOrganisationName("");
      setMembershipNumber("");
      setMembershipStatus("active");
      setJoinDate("");
      setRenewalDate("");
      setExpiryDate("");
      setAnnualFee("");
      setBenefitsSummary("");
      setContactName("");
      setContactEmail("");
      setContactPhone("");
      setCertificateUrl("");
    }
  }, [open, editingMembership]);

  const isValid =
    membershipType.length > 0 && organisationName.trim().length > 0;

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
  };

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  const createMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest(
        "POST",
        "/api/compliance/memberships",
        {
          membershipType,
          organisationName: organisationName.trim(),
          membershipNumber: membershipNumber.trim() || undefined,
          membershipStatus,
          joinDate: joinDate || undefined,
          renewalDate: renewalDate || undefined,
          expiryDate: expiryDate || undefined,
          annualFee: annualFee || undefined,
          benefitsSummary: benefitsSummary.trim() || undefined,
          contactName: contactName.trim() || undefined,
          contactEmail: contactEmail.trim() || undefined,
          contactPhone: contactPhone.trim() || undefined,
          certificateUrl: certificateUrl.trim() || undefined,
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(
          (err as { error?: string }).error ?? "Failed to create membership"
        );
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/compliance/memberships"],
      });
      toast({ title: "Membership created successfully" });
      handleOpenChange(false);
      onSuccess?.();
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const editMutation = useMutation({
    mutationFn: async () => {
      if (!editingMembership?.id) throw new Error("No membership ID");
      const response = await apiRequest(
        "PATCH",
        `/api/compliance/memberships/${editingMembership.id}`,
        {
          membershipType,
          organisationName: organisationName.trim(),
          membershipNumber: membershipNumber.trim() || undefined,
          membershipStatus,
          joinDate: joinDate || undefined,
          renewalDate: renewalDate || undefined,
          expiryDate: expiryDate || undefined,
          annualFee: annualFee || undefined,
          benefitsSummary: benefitsSummary.trim() || undefined,
          contactName: contactName.trim() || undefined,
          contactEmail: contactEmail.trim() || undefined,
          contactPhone: contactPhone.trim() || undefined,
          certificateUrl: certificateUrl.trim() || undefined,
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(
          (err as { error?: string }).error ?? "Failed to update membership"
        );
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/compliance/memberships"],
      });
      toast({ title: "Membership updated successfully" });
      handleOpenChange(false);
      onSuccess?.();
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const isPending = createMutation.isPending || editMutation.isPending;

  const handleSubmit = () => {
    if (!isValid) return;
    if (isEditMode) {
      editMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="modal-membership-form"
      >
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? "Edit Membership" : "Add Membership"}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? "Update the membership details below."
              : "Enter the membership details below."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          {/* Row 1: Type + Status */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="membership-type">
                Type <span className="text-destructive">*</span>
              </Label>
              <Select
                value={membershipType}
                onValueChange={setMembershipType}
              >
                <SelectTrigger
                  id="membership-type"
                  data-testid="select-membership-type"
                >
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {MEMBERSHIP_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="membership-status">Status</Label>
              <Select
                value={membershipStatus}
                onValueChange={setMembershipStatus}
              >
                <SelectTrigger
                  id="membership-status"
                  data-testid="select-membership-status"
                >
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {MEMBERSHIP_STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Organisation Name */}
          <div className="space-y-1.5">
            <Label htmlFor="organisation-name">
              Organisation Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="organisation-name"
              data-testid="input-organisation-name"
              value={organisationName}
              onChange={(e) => setOrganisationName(e.target.value)}
              placeholder="e.g. MFAA, CAFBA, AFG"
            />
          </div>

          {/* Membership Number */}
          <div className="space-y-1.5">
            <Label htmlFor="membership-number">Membership Number</Label>
            <Input
              id="membership-number"
              data-testid="input-membership-number"
              value={membershipNumber}
              onChange={(e) => setMembershipNumber(e.target.value)}
              placeholder="e.g. MFAA-12345"
            />
          </div>

          {/* Dates row */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="join-date">Join Date</Label>
              <Input
                id="join-date"
                data-testid="input-join-date"
                type="date"
                value={joinDate}
                onChange={(e) => setJoinDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="renewal-date">Renewal Date</Label>
              <Input
                id="renewal-date"
                data-testid="input-renewal-date"
                type="date"
                value={renewalDate}
                onChange={(e) => setRenewalDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="expiry-date">Expiry Date</Label>
              <Input
                id="expiry-date"
                data-testid="input-expiry-date"
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
              />
            </div>
          </div>

          {/* Annual Fee */}
          <div className="space-y-1.5">
            <Label htmlFor="annual-fee">Annual Fee ($)</Label>
            <Input
              id="annual-fee"
              data-testid="input-annual-fee"
              type="number"
              value={annualFee}
              onChange={(e) => setAnnualFee(e.target.value)}
              placeholder="0.00"
            />
          </div>

          {/* Contact details */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="contact-name">Contact Name</Label>
              <Input
                id="contact-name"
                data-testid="input-contact-name"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-email">Contact Email</Label>
              <Input
                id="contact-email"
                data-testid="input-contact-email"
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-phone">Contact Phone</Label>
              <Input
                id="contact-phone"
                data-testid="input-contact-phone"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
              />
            </div>
          </div>

          {/* Certificate URL */}
          <div className="space-y-1.5">
            <Label htmlFor="certificate-url">Certificate URL</Label>
            <Input
              id="certificate-url"
              data-testid="input-certificate-url"
              value={certificateUrl}
              onChange={(e) => setCertificateUrl(e.target.value)}
              placeholder="https://sharepoint.com/..."
            />
          </div>

          {/* Benefits Summary */}
          <div className="space-y-1.5">
            <Label htmlFor="benefits-summary">Benefits Summary</Label>
            <Textarea
              id="benefits-summary"
              data-testid="textarea-benefits-summary"
              value={benefitsSummary}
              onChange={(e) => setBenefitsSummary(e.target.value)}
              rows={3}
              placeholder="Key benefits of this membership..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isValid || isPending}
            data-testid="button-submit-membership"
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditMode ? "Save Changes" : "Add Membership"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
